import { Alert } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import useSiteMetadata from '../../hooks/useSiteMetadata'
import { CartContext } from '../../lib/cart'
import { productOptionValueFromCartItem } from '../../lib/cart/item'
import {
  ackAutomaticallyRemovedCartItems,
  cartItemCheckSetFromFeatureFlags,
  requestCheckCartItemsNeedingRemoval,
  useAutoRemoveErrantCartItems,
  useItemsAutomaticallyRemoved,
} from '../../lib/cart/state/cart-items/cart-item-remove'
import {
  requestCheckCartItemsNeedingUpdate,
  useAutoUpdateErrantCartItems,
  useCartItemsUpdating,
} from '../../lib/cart/state/cart-items/cart-item-update'
import {
  AutoRemoveCartItemError,
  CartItemSimpleProduct,
  CartItemVirtualProduct,
} from '../../lib/cart/state/types'
import { reduceIterable } from '../../utils/collectionTools'

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 20px;
`

const ProgressMessage = styled.span`
  margin-top: 10px;
`

/**
 * Make a matching reducer for each CartItemError type. This is used to reduce errors while
 * ensuring a handler is added for each error type. The reducer is called with the accumulator and
 * error object narrowed to the correct type.
 */
const mkCartItemErrorReducer =
  <A,>(reduceByError: {
    [P in AutoRemoveCartItemError['error']]: (
      acc: A,
      error: Extract<AutoRemoveCartItemError, { error: P }>,
    ) => A
  }) =>
  (acc: A, error: AutoRemoveCartItemError): A => {
    const reducer = reduceByError[error.error]
    return reducer(acc, error as Extract<AutoRemoveCartItemError, { error: typeof error }>)
  }

interface CartItemErrorsReduced {
  labelDesignFeeDuplicated: Array<CartItemVirtualProduct>
  labelDesignFeeMissingProduct: Array<CartItemVirtualProduct>
  labelDesignFeeNotRequired: Array<CartItemVirtualProduct>
  productsMissingLabelDesignFee: Array<CartItemSimpleProduct>
}

export interface CartItemErrorsProps {
  className?: string
}

export const CartItemErrors: React.FC<CartItemErrorsProps> = ({ className }) => {
  const { cart } = useContext(CartContext)
  const { featureFlags } = useSiteMetadata() ?? {}
  const { loading: autoRemoveItemsLoading } = useAutoRemoveErrantCartItems(
    cartItemCheckSetFromFeatureFlags(featureFlags),
  )
  const { loading: autoUpdateItemsLoading } = useAutoUpdateErrantCartItems()
  const autoRemovedCartItems = useItemsAutomaticallyRemoved()
  const cartItemsUpdating = useCartItemsUpdating()

  const areItemsUpdatingNotInCart = useMemo(
    () =>
      cartItemsUpdating.length > 0 &&
      !((cart || {}).items || [])?.some(
        (item) => item?.product?.sku && cartItemsUpdating.includes(item.product.sku),
      ),
    [cart, cartItemsUpdating],
  )

  // trigger cart item error check on mount
  useEffect(() => {
    requestCheckCartItemsNeedingRemoval()
    requestCheckCartItemsNeedingUpdate()
  }, [])

  const reduceAutoRemoved = reduceIterable(
    [],
    (
      acc: React.ReactElement[],
      [key, cartItemErrors]: [number, ReadonlyArray<AutoRemoveCartItemError>],
    ) => {
      const {
        labelDesignFeeDuplicated,
        labelDesignFeeMissingProduct,
        labelDesignFeeNotRequired,
        productsMissingLabelDesignFee,
      } = cartItemErrors.reduce(
        mkCartItemErrorReducer<CartItemErrorsReduced>({
          LabelDesignFeeDuplicated: (cartItemErrorsAcc, { cartItem }) => {
            cartItemErrorsAcc.labelDesignFeeDuplicated.push(cartItem)
            return cartItemErrorsAcc
          },
          LabelDesignFeeMissingProduct: (cartItemErrorsAcc, { cartItem }) => {
            cartItemErrorsAcc.labelDesignFeeMissingProduct.push(cartItem)
            return cartItemErrorsAcc
          },
          LabelDesignFeeNotRequired: (cartItemErrorsAcc, { cartItem }) => {
            cartItemErrorsAcc.labelDesignFeeNotRequired.push(cartItem)
            return cartItemErrorsAcc
          },
          ProductMissingLabelDesignFee: (cartItemErrorsAcc, { cartItem }) => {
            cartItemErrorsAcc.productsMissingLabelDesignFee.push(cartItem)
            return cartItemErrorsAcc
          },
        }),
        {
          labelDesignFeeDuplicated: [],
          labelDesignFeeMissingProduct: [],
          labelDesignFeeNotRequired: [],
          productsMissingLabelDesignFee: [],
        },
      )

      acc.push(
        <Alert key={key} type="attention" onDismiss={() => ackAutomaticallyRemovedCartItems(key)}>
          {labelDesignFeeDuplicated.length > 0 && (
            <>
              These items were duplicated and were removed from your cart:
              <ul>
                {labelDesignFeeDuplicated.map((cartItem) => (
                  <li key={cartItem.id}>
                    {cartItem.product.name} for {productOptionValueFromCartItem(cartItem)}
                  </li>
                ))}
              </ul>
            </>
          )}
          {labelDesignFeeMissingProduct.length > 0 && (
            <>
              These items are no longer required and were removed from your cart:
              <ul>
                {labelDesignFeeMissingProduct.map((cartItem) => (
                  <li key={cartItem.id}>
                    {cartItem.product.name} for {productOptionValueFromCartItem(cartItem)}
                  </li>
                ))}
              </ul>
            </>
          )}
          {labelDesignFeeNotRequired.length > 0 && (
            <>
              These items are no longer required and were removed from your cart:
              <ul>
                {labelDesignFeeNotRequired.map((cartItem) => (
                  <li key={cartItem.id}>
                    {cartItem.product.name} for {productOptionValueFromCartItem(cartItem)}
                  </li>
                ))}
              </ul>
            </>
          )}
          {productsMissingLabelDesignFee.length > 0 && (
            <>
              These products require a Graphic Design Fee item and were removed from your cart:
              <ul>
                {productsMissingLabelDesignFee.map((cartItem) => (
                  <li key={cartItem.id}>{cartItem.product.name}</li>
                ))}
              </ul>
            </>
          )}
          Please check your cart before proceeding with checkout.
        </Alert>,
      )
      return acc
    },
  )

  return (
    <div className={className}>
      {areItemsUpdatingNotInCart && !(autoRemoveItemsLoading || autoUpdateItemsLoading) && (
        <ProgressWrapper>
          <CircularProgress color="primary" />
          <ProgressMessage>Updating your cart...</ProgressMessage>
        </ProgressWrapper>
      )}
      {(autoRemoveItemsLoading || autoUpdateItemsLoading) && (
        <ProgressWrapper>
          <CircularProgress color="primary" />
          <ProgressMessage>Checking your cart for errors...</ProgressMessage>
        </ProgressWrapper>
      )}
      {reduceAutoRemoved(autoRemovedCartItems)}
    </div>
  )
}
